import { lazy } from "react";

import { Navigate } from "react-router-dom";

// Kickoff refinement A: static, so the boundary can never be the chunk that
// failed to load.
import KickoffRouteBoundary from "../components/Kickoff/KickoffRouteBoundary";
// Keep auth pages static (needed for initial render)
import { ForgotPassword } from "../pages/ForgotPassword";
import { Login } from "../pages/Login";
import { Register } from "../pages/Register";

// Lazy load heavy pages for code splitting
const Dashboard = lazy(() =>
  import("../pages/Dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
// FE2: the Google OAuth return routes. The backend callback 303s to
// `{origin}/work` or `{origin}/squad` (plus `?google=cancelled|failed`); this
// page hands the learner back to Dashboard with the return hint's key.
const WorkspaceGoogleReturn = lazy(() =>
  import("../pages/WorkspaceGoogleReturn").then((m) => ({
    default: m.WorkspaceGoogleReturn,
  })),
);
const Performance = lazy(() =>
  import("../pages/Performance").then((m) => ({
    default: m.Performance,
  })),
);
const Season = lazy(() =>
  import("../pages/Season").then((m) => ({
    default: m.Season,
  })),
);
const SeasonDetails = lazy(() =>
  import("../pages/Season/SeasonDetails").then((m) => ({
    default: m.SeasonDetails,
  })),
);
const Task = lazy(() =>
  import("../pages/Task").then((m) => ({
    default: m.Task,
  })),
);
const Journey = lazy(() =>
  import("../pages/Journey").then((m) => ({
    default: m.Journey,
  })),
);
// v4.0 Lesson Experience F4.2: ReflectionPage full-page route. v4 cohorts
// (usesV4Experience=true) open Reflection at /challenges/:challengeId/reflection/:lessonId
// instead of the legacy drawer. The page self-gates on usesV4Experience and
// redirects non-v4 traffic to /dashboard so legacy challenges keep the
// drawer flow.
const ReflectionPage = lazy(() =>
  import("../pages/Reflection/ReflectionPage").then((m) => ({
    default: m.ReflectionPage,
  })),
);
// v4.0 Lesson Experience F7: cinematic kickoff route. The kickoff plays
// once per (user, challenge) before mission board access; the route
// /challenges/:challengeId/kickoff renders the full-screen cinematic
// surface (Layout suppresses the Sidebar for this path; see Layout.jsx).
const KickoffPage = lazy(() =>
  import("../pages/Kickoff/KickoffPage").then((m) => ({
    default: m.KickoffPage,
  })),
);
// Credential Act-1 training journey (F1b). Full-page surface family that
// renders entirely off the clearance read endpoint - ModePlayer untouched.
// Credential cohorts route here from the dashboard journey (entry routing in
// F1b sub-chunk 4); non-credential cohorts never reach it.
const CredentialTraining = lazy(() =>
  import("../pages/CredentialTraining").then((m) => ({
    default: m.CredentialTraining,
  })),
);
const CredentialUnit = lazy(() =>
  import("../pages/CredentialTraining/CredentialUnit").then((m) => ({
    default: m.CredentialUnit,
  })),
);
const Leaderboard = lazy(() =>
  import("../pages/Leaderboard/Leaderboard").then((m) => ({
    default: m.Leaderboard,
  })),
);
const Profile = lazy(() =>
  import("../pages/Profile").then((m) => ({
    default: m.Profile,
  })),
);
const Bookmark = lazy(() =>
  import("../pages/Bookmark").then((m) => ({
    default: m.Bookmark,
  })),
);
const Notification = lazy(() =>
  import("../pages/Notification").then((m) => ({
    default: m.Notification,
  })),
);
const Tutorial = lazy(() =>
  import("../pages/Tutorial").then((m) => ({
    default: m.Tutorial,
  })),
);
const Feedback = lazy(() =>
  import("../pages/Feedback").then((m) => ({
    default: m.Feedback,
  })),
);

// Admin pages
const AdminDashboard = lazy(() => import("../pages/Admin/Dashboard"));
const AdminProfile = lazy(() =>
  import("../pages/Admin/Profile").then((m) => ({
    default: m.AdminProfile,
  })),
);
const AdminLeaderboard = lazy(() =>
  import("../pages/Admin/Leaderboard").then((m) => ({
    default: m.AdminLeaderboard,
  })),
);
const AdminManage = lazy(() =>
  import("../pages/Admin/Manage").then((m) => ({
    default: m.AdminManage,
  })),
);
const AdminDeliverables = lazy(() =>
  import("../pages/Admin/Deliverables").then((m) => ({
    default: m.AdminDeliverables,
  })),
);

// FACILITATOR PREP. No longer development-only.
//
// It used to be double-gated to development, at the lazy AND at route
// registration, because everything it renders is say-aloud staged facilitation
// material a cohort apprentice must not pre-read, and the admin route flag in
// App.jsx is fall-through rather than fail-closed. An "isAdmin" route was never
// containment.
//
// The containment story is now the server's, which is what the gate was always
// waiting for: a fail-closed, server-authorized facilitator read that withholds
// the staged calibration SERVER SIDE, so withheld content is structurally absent
// from the response instead of hidden in a client anyone can read. The page
// carries no fixture: it fetches, and the build-time containment gate scans the
// emitted bundle for the fixture's canary strings.
const AdminPrep = lazy(() =>
  import("../pages/Admin/Prep").then((m) => ({ default: m.AdminPrep })),
);

export const router = [
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
    isAuth: true,
  },
  {
    path: "/work",
    element: <WorkspaceGoogleReturn />,
    isAuth: true,
  },
  {
    path: "/squad",
    element: <WorkspaceGoogleReturn />,
    isAuth: true,
  },
  {
    // F1b: Act-1 credential training journey strip. Level-scoped by capsuleId.
    path: "/dashboard/training/:capsuleId",
    element: <CredentialTraining />,
    isAuth: true,
  },
  {
    // F1b: Act-1 node surface (cert-unit reader + floor test). Beat-scoped.
    path: "/dashboard/training/:capsuleId/unit/:beatId",
    element: <CredentialUnit />,
    isAuth: true,
  },
  {
    path: "/leaderboard",
    element: <Leaderboard />,
    isAuth: true,
  },
  {
    path: "/bookmark",
    element: <Bookmark />,
    isAuth: true,
  },
  {
    path: "/profile",
    element: <Profile />,
  },
  {
    path: "/season",
    element: <Season />,
  },
  {
    path: "/season/:id",
    element: <SeasonDetails />,
  },
  {
    path: "/tasks",
    element: <Task />,
    isAuth: true,
  },
  {
    path: "/journey",
    element: <Journey />,
    isAuth: true,
  },
  {
    // v4.0 Lesson Experience F4.2: full-page Reflection route for v4 cohorts.
    // lessonId in the route identifies which module's reflection this is.
    // The page resolves moduleId from the capsule tree on mount.
    path: "/challenges/:challengeId/reflection/:lessonId",
    element: <ReflectionPage />,
    isAuth: true,
  },
  {
    // v4.0 Lesson Experience F7: cinematic challenge kickoff. Plays once
    // per (user, challenge) before any mission board access. The route
    // is reachable directly OR via KickoffGuard redirect from
    // /challenges/:challengeId. See Layout.jsx for sidebar suppression
    // on this path.
    path: "/challenges/:challengeId/kickoff",
    element: (
      <KickoffRouteBoundary>
        <KickoffPage />
      </KickoffRouteBoundary>
    ),
    isAuth: true,
  },
  {
    path: "/notifications",
    element: <Notification />,
    isAuth: true,
  },
  {
    path: "/performance",
    element: <Performance />,
    isAuth: true,
  },
  {
    path: "/tutorial",
    element: <Tutorial />,
    isAuth: true,
  },
  {
    path: "/feedback",
    element: <Feedback />,
    isAuth: true,
  },
  // Admin routes
  {
    path: "/admin/dashboard",
    element: <AdminDashboard />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/profile",
    element: <AdminProfile />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/leaderboard",
    element: <AdminLeaderboard />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/manage",
    element: <AdminManage />,
    isAuth: true,
    isAdmin: true,
  },
  // THE TWO RETIRED SURFACES, KEPT IN THE TABLE AS REDIRECTS.
  //
  // Deleting the entries outright would have been the smaller diff and the
  // wrong one: `App.jsx`'s catch-all is `<Navigate to="/login" replace />`, so
  // a bookmark to `/admin/students` would have logged a facilitator OUT of a
  // surface they were entitled to, with no error anywhere to explain it. The
  // tests therefore assert the DESTINATION, because a fall-through to login
  // errors nothing and would pass a "does not crash" test.
  //
  // `replace` because these paths are gone: without it the back button lands
  // on the retired path, which redirects forward again, and a facilitator
  // cannot leave the surface by going back.
  //
  // The page components under `pages/Admin/Students` and `pages/Admin/Squads`
  // were unrouted here under ruling D6, which deferred deleting them until the
  // promoted Manage routes were live and Manage had demonstrably replaced the
  // capability. That has happened, and FE-9.0 deleted all four files. These two
  // redirects are what is left of those surfaces, and they stay: a bookmark to
  // either path must still land a facilitator on Manage.
  {
    path: "/admin/students",
    element: <Navigate to="/admin/manage" replace />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/squads",
    element: <Navigate to="/admin/manage" replace />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/deliverables",
    element: <AdminDeliverables />,
    isAuth: true,
    isAdmin: true,
  },
  {
    path: "/admin/prep",
    element: <AdminPrep />,
    isAuth: true,
    isAdmin: true,
  },
];
